import { addDays, format, parse } from 'date-fns'

/**
 * Every time in the wizard belongs to the vendor, not to the phone holding it.
 * Two rules keep that true:
 *
 *  1. Slot strings arrive as ISO with the vendor offset ("...T10:30:00+07:00").
 *     Their wall clock is read straight off the string, never through `Date`,
 *     so a customer travelling abroad still sees the salon's 10:30.
 *  2. "Today" comes from Intl with an explicit timeZone, so the date strip
 *     starts on the vendor's today even across the date line.
 *
 * `yyyy-MM-dd` strings are treated as pure calendar dates and only ever turned
 * into a Date at local noon, which no offset can push into a neighbouring day.
 */

const ISO_PARTS = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/

export interface WallClock {
  /** `yyyy-MM-dd` */
  date: string
  hour: number
  minute: number
}

export function parseWallClock(iso: string): WallClock | null {
  const m = ISO_PARTS.exec(iso)
  if (!m) return null
  return { date: `${m[1]}-${m[2]}-${m[3]}`, hour: Number(m[4]), minute: Number(m[5]) }
}

/** "09:00" in the vendor's own clock. 24h reads unambiguously at a glance. */
export function formatSlotTime(iso: string): string {
  const wc = parseWallClock(iso)
  if (!wc) return ''
  return `${String(wc.hour).padStart(2, '0')}:${String(wc.minute).padStart(2, '0')}`
}

export function slotDate(iso: string): string {
  return parseWallClock(iso)?.date ?? ''
}

/** Today in the vendor timezone as `yyyy-MM-dd`. */
export function todayInZone(timeZone: string): string {
  try {
    // en-CA formats as yyyy-MM-dd, which is exactly the key format we store.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date())
  } catch {
    return format(new Date(), 'yyyy-MM-dd')
  }
}

/** A `yyyy-MM-dd` key as a local-noon Date, safe for date-fns formatting. */
export function calendarDate(key: string): Date {
  const d = parse(key, 'yyyy-MM-dd', new Date())
  d.setHours(12, 0, 0, 0)
  return d
}

export function dateKey(d: Date): string {
  return format(d, 'yyyy-MM-dd')
}

export function addDaysKey(key: string, days: number): string {
  return dateKey(addDays(calendarDate(key), days))
}

/** `yyyy-MM` of a date key, for the month-scoped availability query. */
export function monthKey(key: string): string {
  return key.slice(0, 7)
}

/** "Mon", "Tue" — the short weekday the date strip shows above each number. */
export function weekdayShort(key: string): string {
  return format(calendarDate(key), 'EEE')
}

export function dayOfMonth(key: string): string {
  return format(calendarDate(key), 'd')
}

export function monthShort(key: string): string {
  return format(calendarDate(key), 'MMMM yyyy')
}

/** "Thursday, 15 September 2026" — the long form used on review and confirmation. */
export function formatLongDate(key: string): string {
  return format(calendarDate(key), 'EEEE, d MMMM yyyy')
}

/** Adds minutes to a wall clock without leaving the vendor timezone: the offset
 *  suffix of the original string is carried over untouched. */
export function addMinutesToIso(iso: string, minutes: number): string {
  const wc = parseWallClock(iso)
  if (!wc) return iso
  const base = calendarDate(wc.date)
  base.setHours(wc.hour, wc.minute + minutes, 0, 0)
  const offset = iso.slice(16).replace(/^:\d{2}(\.\d+)?/, '') || ''
  return `${format(base, "yyyy-MM-dd'T'HH:mm:ss")}${offset}`
}

/** Morning/afternoon/evening grouping, as the slot grid in the design does. */
export type SlotBand = 'morning' | 'afternoon' | 'evening'

export const BAND_LABEL: Record<SlotBand, string> = {
  morning: 'Morning',
  afternoon: 'Afternoon',
  evening: 'Evening',
}

export const BAND_ICON: Record<SlotBand, string> = {
  morning: 'light_mode',
  afternoon: 'routine',
  evening: 'dark_mode',
}

export function bandOf(iso: string): SlotBand {
  const hour = parseWallClock(iso)?.hour ?? 0
  if (hour < 12) return 'morning'
  if (hour < 17) return 'afternoon'
  return 'evening'
}
